/**
 * m3 echo Frontend - API Client
 *
 * Zentraler HTTP-Client für Backend-Kommunikation.
 *
 * Security improvements (v4.5.0):
 * - Access tokens stored in-memory only (via auth store)
 * - Refresh tokens handled via HttpOnly cookies
 * - Automatic token refresh before expiry
 * - withCredentials for cookie handling
 */

import axios, { AxiosInstance } from 'axios';
import { useAuthStore } from '../stores/useAuthStore';
import { getApiBaseUrl } from './apiBaseUrl';
import type {
  BenchmarkCostPreview,
  BenchmarkLineupResponse,
  GroundingTestResult,
  LayerConfiguration,
} from '../types/api';
import type {
  TeaserAnalysis,
  TeaserAnalysisCreate,
  TeaserAnalysisList,
  TeaserDefaultPrompt,
  TeaserModelOption,
} from '../types/teaser';

export const API_BASE_URL = getApiBaseUrl();

/**
 * Axios-Instanz mit Basis-Konfiguration
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 30000, // 30 Sekunden Timeout
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // IMPORTANT: Enable cookies for refresh token
});

/**
 * Request Interceptor - adds Auth token from in-memory store
 */
apiClient.interceptors.request.use(
  (config) => {
    // Get token from in-memory auth store (NOT localStorage)
    const accessToken = useAuthStore.getState().accessToken;
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor - handles 401 errors and token refresh
 */
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh using the HttpOnly cookie
        // The cookie is automatically sent with withCredentials: true
        const { authService } = await import('./auth');
        const response = await authService.refreshToken();

        // Update auth store with new token (in-memory only)
        const authStore = useAuthStore.getState();
        authStore.setAuth(response);

        // Update WebSocket with new token to prevent stale token on reconnect
        import('./websocket').then(({ websocketService }) => {
          websocketService.updateToken(response.access_token);
        });

        // Update authorization header and process queued requests
        originalRequest.headers['Authorization'] = 'Bearer ' + response.access_token;
        processQueue(null, response.access_token);

        isRefreshing = false;
        return apiClient(originalRequest);

      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;

        // Clear auth state (in-memory)
        useAuthStore.getState().clearAuth();

        // Redirect to login page
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      }
    }

    // Handle 503 maintenance mode
    if (error.response?.status === 503 && error.response?.data?.detail === 'maintenance_mode') {
      useAuthStore.getState().setMaintenanceMode(true);
      return Promise.reject(error);
    }

    // Handle 403 password_change_required.
    //
    // The backend restricts a session with a pending password change to the
    // change-password flow itself and refuses everything else here. Without
    // this branch a component that calls the API directly - rather than going
    // through ProtectedRoute, which already redirects on the user flag - would
    // only surface the raw code as an error message.
    //
    // Deliberately after the 401 branch and on a different status, so the two
    // cannot interfere: a 401 is still refreshed and retried, and only a
    // request that comes back 403 with this exact marker lands here.
    //
    // No logout and no reload loop: the store flag is what /change-password and
    // ProtectedRoute read, and the redirect is skipped when the browser is
    // already there. The routes that stay reachable in this state
    // (/api/auth/me, /api/auth/change-password, /api/auth/logout,
    // /api/auth/refresh) never answer with this code, so the page the user is
    // sent to cannot bounce them straight back.
    if (
      error.response?.status === 403 &&
      error.response?.data?.detail === 'password_change_required'
    ) {
      useAuthStore.getState().updateMustChangePassword(true);

      if (typeof window !== 'undefined' && window.location.pathname !== '/change-password') {
        window.location.href = '/change-password';
      }

      return Promise.reject(error);
    }

    // Handle other errors
    let errorMessage = 'An unexpected error occurred';
    let errorCode = undefined;

    if (error.response) {
      // Server hat mit Fehlercode geantwortet
      errorCode = error.response.status;
      const detail = error.response.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ');
      } else {
        errorMessage = error.response.data?.message || error.message;
      }
      console.error(`API Error [${errorCode}]:`, errorMessage);
    } else if (error.request) {
      // Request wurde gesendet, aber keine Antwort erhalten
      errorMessage = 'Network error: No response from server';
      console.error('Network Error:', error.message);
    } else {
      // Fehler beim Setup des Requests
      errorMessage = error.message;
      console.error('Error:', error.message);
    }

    // Return structured error for better error handling
    return Promise.reject({
      message: errorMessage,
      code: errorCode,
      originalError: error
    });
  }
);

/**
 * Turn a rejection from the interceptor above into something worth showing.
 *
 * It lives here, beside the interceptor, because the interceptor's rejection
 * shape is the reason it is needed: the handler does not re-reject the axios
 * error, it rejects with `{message, code, originalError}`. A caller reading
 * `err.response` therefore gets `undefined` and the server's body sits one
 * level deeper - a trap that had already been walked into twice, in two
 * components, with two copies of the same unwrapping.
 *
 * Both locations are read, so the sentence survives whichever shape arrives
 * (a direct axios error, or the interceptor's wrapper). `detail.details` is
 * read as well: `QuotaExceededException` puts its worded reason there, naming
 * which check failed and by how much, while `detail.error` is only an English
 * tag ("Autopilot quota exceeded") and is deliberately skipped so the German
 * fallback reads better.
 *
 * `message` is the last resort before the caller's fallback, and it is not
 * redundant: the interceptor already flattens a string `detail` and joins a
 * 422 validation array into it, and it is the only place a network failure
 * ("Network error: No response from server") is described at all.
 */
export function describeApiError(err: unknown, fallback: string): string {
  if (!err || typeof err !== 'object') return fallback;
  const candidate = err as {
    message?: unknown;
    response?: any;
    originalError?: { response?: any };
  };
  const detail =
    candidate.response?.data?.detail ?? candidate.originalError?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object') {
    const details = (detail as Record<string, unknown>).details;
    if (typeof details === 'string' && details.trim()) return details;
  }
  return typeof candidate.message === 'string' && candidate.message.trim()
    ? candidate.message
    : fallback;
}

// ===========================
// Prompts API
// ===========================

export const promptsApi = {
  /**
   * Gibt alle Prompts zurück
   */
  getAll: (params?: { skip?: number; limit?: number; tenant_id?: number }) =>
    apiClient.get('/prompts/', { params }),

  /**
   * Gibt einen einzelnen Prompt zurück
   */
  getById: (id: number) => apiClient.get(`/prompts/${id}`),

  /**
   * Erstellt einen neuen Prompt
   */
  create: (data: { content: string; placeholders?: string[] }, tenant_id?: number) =>
    apiClient.post('/prompts/', data, { params: tenant_id ? { tenant_id } : {} }),

  /**
   * Aktualisiert einen Prompt
   */
  update: (id: number, data: { content?: string; placeholders?: string[] }) =>
    apiClient.put(`/prompts/${id}`, data),

  /**
   * Löscht einen Prompt
   */
  delete: (id: number) => apiClient.delete(`/prompts/${id}`),
};

// ===========================
// Projects API
// ===========================

export const projectsApi = {
  /**
   * Zitat-Anteil (Kennzahlen, Verlauf, Domainranking)
   */
  getCitations: (id: number, params: Record<string, string | number | undefined>, signal?: AbortSignal) =>
    apiClient.get(`/projects/${id}/citations`, { params, signal }),

  getCitationDomains: (id: number) => apiClient.get(`/projects/${id}/citation-domains`),

  updateCitationDomains: (
    id: number,
    data: { expected_revision: string | null; domains: { domain: string; kind: string; match: string }[] }
  ) => apiClient.patch(`/projects/${id}/citation-domains`, data),

  /**
   * Gibt alle Projekte zurück
   */
  getAll: (params?: { skip?: number; limit?: number; search_term_filter?: string; tenant_id?: number }) =>
    apiClient.get('/projects/', { params }),

  /**
   * Gibt ein einzelnes Projekt mit allen Jobs und Statistiken zurück
   */
  getById: (id: number) => apiClient.get(`/projects/${id}`),

  /**
   * Gibt Statistiken für ein Projekt zurück
   */
  getStatistics: (id: number) => apiClient.get(`/projects/${id}/statistics`),

  /**
   * Gibt Per-Prompt Breakdown für ein Projekt zurück
   */
  getPromptBreakdown: (id: number) => apiClient.get(`/projects/${id}/prompt-breakdown`),

  /**
   * Erstellt ein neues Projekt mit erstem Job
   */
  create: (data: {
    name: string;
    description?: string;
    prompts: { prompt_id: number; prompt_values: Record<string, string> }[];
    search_term: string;
    search_term_synonym1?: string;  // v4.8.1
    search_term_synonym2?: string;  // v4.8.1
  }, tenant_id?: number) => apiClient.post('/projects/', data, { params: tenant_id ? { tenant_id } : {} }),

  /**
   * Erstellt einen Re-Test für ein Projekt
   */
  retest: (id: number) => apiClient.post(`/projects/${id}/retest`),

  /**
   * Exportiert Projekt-Daten als CSV
   */
  exportCSV: (id: number) =>
    apiClient.get(`/projects/${id}/export/csv`, {
      responseType: 'blob', // Important for binary data
    }),

  /**
   * Löscht ein Projekt
   */
  delete: (id: number) => apiClient.delete(`/projects/${id}`),

  /**
   * Gibt Heatmap-Daten für ein Projekt zurück (Prompt-Scores über Zeit)
   */
  getHeatmapData: (id: number, params?: { model_name?: string; limit?: number }) =>
    apiClient.get(`/projects/${id}/heatmap-data`, { params }),
};

// ===========================
// Jobs API
// ===========================

export const jobsApi = {
  /**
   * Gibt alle Jobs zurück
   */
  getAll: (params?: { skip?: number; limit?: number; status_filter?: string }) =>
    apiClient.get('/jobs/', { params }),

  /**
   * Gibt einen einzelnen Job mit Details zurück
   */
  getById: (id: number) => apiClient.get(`/jobs/${id}`),

  /**
   * Gibt Dashboard-Statistiken zurück
   */
  getStatistics: () => apiClient.get('/jobs/statistics'),

  /**
   * Erstellt einen neuen Job
   */
  create: (data: {
    prompts?: { prompt_id: number; prompt_values: Record<string, string> }[];
    search_term: string;
    search_term_synonym1?: string;
    search_term_synonym2?: string;
    project_id?: number;
  }) => apiClient.post('/jobs/', data),

  /**
   * Erstellt einen Re-Test
   */
  retest: (id: number) => apiClient.post(`/jobs/${id}/retest`),

  /**
   * Gibt alle Re-Tests eines Jobs zurück
   */
  getRetests: (id: number) => apiClient.get(`/jobs/${id}/retests`),

  /**
   * Vergleicht einen Original-Job mit einem Re-Test
   */
  compareJobs: (originalId: number, retestId: number) =>
    apiClient.get(`/jobs/${originalId}/compare/${retestId}`),

  /**
   * Gibt die vollständige Verlaufskette eines Jobs zurück (Original + alle Re-Tests)
   */
  getHistory: (id: number) => apiClient.get(`/jobs/${id}/history`),

  /**
   * Löscht einen Job
   */
  delete: (id: number) => apiClient.delete(`/jobs/${id}`),
};

// ===========================
// Settings API
// ===========================

export const settingsApi = {
  /**
   * Gibt verfügbare KI-Modelle zurück
   */
  getModels: () => apiClient.get('/settings/models/'),

  /**
   * Gibt verschleierte API-Keys zurück mit Token-Usage
   */
  getApiKeys: (params?: { tenant_id?: number }) =>
    apiClient.get('/settings/api-keys/', { params }),

  /**
   * Testet einen API-Key
   */
  testApiKey: (provider: string, apiKey: string) =>
    apiClient.post('/settings/test-api-key', { provider, api_key: apiKey }),

  /**
   * Health Check für Settings
   */
  healthCheck: () => apiClient.get('/settings/health-check'),

  /**
   * Gibt aktuelle Layer-Konfiguration zurück
   */
  getLayerConfiguration: () => apiClient.get<LayerConfiguration>('/settings/layers/'),

  /**
   * Gemini-Thinking-Level je aktivem Google-Modell (Modelle ohne Stufen fehlen).
   */
  getThinkingLevels: () => apiClient.get<Record<string, string[]>>('/settings/thinking-levels/'),

  /**
   * Aktualisiert Layer-Konfiguration.
   * Fallback-Modelle: `null` wird als leerer String gesendet (kein Fallback).
   * Thinking-Level: `null` wird als leerer String gesendet (Google-Standard).
   */
  updateLayerConfiguration: (config: LayerConfiguration) =>
    apiClient.put<LayerConfiguration>('/settings/layers/', null, {
      params: {
        ...config,
        layer2_fallback_model: config.layer2_fallback_model ?? '',
        layer2_1_fallback_model: config.layer2_1_fallback_model ?? '',
        layer4_fallback_model: config.layer4_fallback_model ?? '',
        layer5_fallback_model: config.layer5_fallback_model ?? '',
        layer6_fallback_model: config.layer6_fallback_model ?? '',
        layer2_thinking_level: config.layer2_thinking_level ?? '',
        layer2_1_thinking_level: config.layer2_1_thinking_level ?? '',
        layer4_thinking_level: config.layer4_thinking_level ?? '',
        layer5_thinking_level: config.layer5_thinking_level ?? '',
        layer6_thinking_level: config.layer6_thinking_level ?? '',
      },
    }),

  /**
   * Testet die Verbindung zu einem spezifischen Modell
   */
  testModelHealth: (modelName: string) =>
    apiClient.post(`/settings/models/${modelName}/test`),

  /**
   * Websuche-Test (Grounding) für ein Modell (Admin only)
   */
  testModelGrounding: (modelName: string) =>
    apiClient.post<GroundingTestResult>(`/settings/models/${modelName}/test`, null, {
      params: { grounding: true },
      timeout: 150000,  // Backend wartet bis zu 120 s (OpenAI-Websuche)
    }),

  /**
   * Testet die Verbindung zu allen Modellen
   */
  testAllModelsHealth: () =>
    apiClient.post('/settings/models/test-all'),

  /**
   * Gibt den Gesundheitsstatus aller Modelle zurück
   */
  getModelsHealth: () =>
    apiClient.get('/settings/models/health'),

  /**
   * Gibt Celery Worker Logs zurück
   */
  getCeleryLogs: (lines: number = 100) =>
    apiClient.get(`/settings/logs/celery?lines=${lines}`),

  /**
   * Gibt alle Modell-Preise zurück
   */
  getModelPricing: () =>
    apiClient.get('/settings/pricing/'),

  /**
   * Gibt Preis für ein spezifisches Modell zurück
   */
  getModelPricingByName: (modelName: string) =>
    apiClient.get(`/settings/pricing/${modelName}`),

  /**
   * Erstellt oder aktualisiert Modell-Preise in Bulk
   */
  bulkUpdateModelPricing: (pricing: { model_name: string; input_price_per_million: number; output_price_per_million: number }[]) =>
    apiClient.post('/settings/pricing/bulk', { pricing }),

  /**
   * Löscht Preis für ein spezifisches Modell
   */
  deleteModelPricing: (modelName: string) =>
    apiClient.delete(`/settings/pricing/${modelName}`),

  /**
   * Bestätigt, dass der Preis eines Modells noch aktuell ist
   */
  verifyModelPricing: (modelName: string) =>
    apiClient.post(`/settings/pricing/${modelName}/verify`),

  /**
   * Gibt alle Jobs in der Warteschlange zurück (Admin only)
   */
  getQueuedJobs: () =>
    apiClient.get('/settings/queue'),

  /**
   * Gibt System-Einstellungen zurück (Admin only)
   */
  getSystemSettings: () =>
    apiClient.get('/settings/system'),

  /**
   * Aktualisiert System-Einstellungen (Admin only)
   */
  updateSystemSettings: (data: {
    disclaimer_enabled?: boolean;
    disclaimer_headline?: string;
    disclaimer_text?: string;
    maintenance_mode?: boolean;
    weekly_report_enabled?: boolean;
    weekly_report_email?: string;
  }) => apiClient.put('/settings/system', data),

  /**
   * Gibt den Aktivierungsstatus aller Modelle zurück
   */
  getModelStatus: () =>
    apiClient.get('/settings/models/status'),

  /**
   * Aktualisiert den Aktivierungsstatus eines Modells (Admin only)
   */
  updateModelStatus: (modelName: string, isActive: boolean) =>
    apiClient.put(`/settings/models/${modelName}/status`, { is_active: isActive }),

  /**
   * Gibt verfügbare Modelle für den aktuellen Benutzer zurück (v4.9)
   * Berücksichtigt globale und Tenant-spezifische Einschränkungen
   */
  getAvailableModels: () =>
    apiClient.get('/settings/models/available/'),

  /**
   * Gibt aktuelles Cheatsheet + Archiv zurück (Admin only)
   */
  getCheatsheet: () =>
    apiClient.get('/settings/cheatsheet/'),

  /**
   * Erstellt eine neue Cheatsheet-Version (Admin only)
   */
  updateCheatsheet: (data: { content: string }) =>
    apiClient.put('/settings/cheatsheet/', data),

  // AI Model Management

  /**
   * Gibt verfügbare Anbieter zurück mit API-Key-Status
   */
  getProviders: () =>
    apiClient.get('/settings/providers/'),

  /**
   * Gibt alle KI-Modelle aus der ai_models Tabelle zurück
   */
  getAIModels: () =>
    apiClient.get('/settings/ai-models/'),

  /**
   * Gibt die aktuell zentral festgelegte Benchmark-Aufstellung eines Mandanten zurück
   * (ohne tenant_id: eigener Mandant; fremde Mandanten nur für Admins)
   */
  getCurrentBenchmarkModels: (tenant_id?: number) =>
    apiClient.get('/settings/benchmark-models/current', {
      params: tenant_id !== undefined ? { tenant_id } : {},
    }),

  /**
   * Alle aktiven Modelle mit Aufstellungs-Flag, Stabilität und Preis-Status (Admin only)
   */
  getBenchmarkLineup: () =>
    apiClient.get<BenchmarkLineupResponse>('/settings/benchmark-models'),

  /**
   * Setzt die zentrale Benchmark-Aufstellung (Admin only)
   */
  setBenchmarkLineup: (codes: string[]) =>
    apiClient.put<{ lineup: string[] }>('/settings/benchmark-models', { model_codes: codes }),

  /**
   * Kostenvorschau für einen Aufstellungs-Entwurf (Admin only)
   */
  previewBenchmarkCost: (codes: string[]) =>
    apiClient.post<BenchmarkCostPreview>('/settings/benchmark-models/cost-preview', {
      model_codes: codes,
    }),

  /**
   * Aktualisiert Anzeigename und/oder Grounding-Flag eines Modells (Admin only)
   */
  updateAIModel: (
    modelCode: string,
    data: { display_name?: string; supports_grounding?: boolean }
  ) => apiClient.patch(`/settings/ai-models/${modelCode}`, data),

  /**
   * Erstellt ein neues KI-Modell (Admin only)
   */
  createAIModel: (data: {
    model_code: string;
    display_name: string;
    provider: string;
    supports_grounding?: boolean;
  }) =>
    apiClient.post('/settings/ai-models/', data),

  /**
   * Löscht ein KI-Modell (Admin only)
   */
  deleteAIModel: (modelCode: string) =>
    apiClient.delete(`/settings/ai-models/${modelCode}`),

  /**
   * Aktualisiert den Aktivierungsstatus eines KI-Modells (Admin only)
   */
  updateAIModelStatus: (modelCode: string, isActive: boolean) =>
    apiClient.put(`/settings/ai-models/${modelCode}/status`, { is_active: isActive }),

  /**
   * Sendet einen Test-Wochenreport (Admin only)
   */
  sendTestWeeklyReport: () =>
    apiClient.post('/settings/system/test-weekly-report'),
};

// ===========================
// Support Tickets API
// ===========================

// ===========================
// Optimization API
// ===========================

export const optimizationApi = {
  /**
   * Prüft ob ein Entwurf für eine Empfehlung existiert
   */
  checkDraft: (projectId: number, testId: number, criterion: string, index: number) =>
    apiClient.get(`/optimization/projects/${projectId}/tests/${testId}/drafts/${criterion}/${index}`),

  /**
   * Generiert einen Entwurf für eine Empfehlung
   */
  generateDraft: (projectId: number, testId: number, criterion: string, index: number, recommendationText: string, recommendationReasoning?: string) =>
    apiClient.post(`/optimization/projects/${projectId}/tests/${testId}/drafts/${criterion}/${index}`, {
      recommendation_text: recommendationText,
      recommendation_reasoning: recommendationReasoning || null
    }, {
      timeout: 120000  // 2 Minuten Timeout für Grounding-Anfragen
    }),
};

export const supportApi = {
  /**
   * Create a new support ticket
   */
  create: (data: {
    title: string;
    message: string;
    attachment_filename?: string;
    attachment_data?: string;
  }) => apiClient.post('/support/', data),

  /**
   * Get all support tickets (admin only)
   */
  getAll: (params?: { skip?: number; limit?: number; status_filter?: string }) =>
    apiClient.get('/support/', { params }),

  /**
   * Get a single support ticket (admin only)
   */
  getById: (id: number) => apiClient.get(`/support/${id}`),

  /**
   * Get unread ticket count (admin only)
   */
  getUnreadCount: () => apiClient.get('/support/unread-count'),

  /**
   * Update a support ticket (admin only)
   */
  update: (id: number, data: { status?: string; is_read?: boolean }) =>
    apiClient.patch(`/support/${id}`, data),

  /**
   * Delete a support ticket (admin only)
   */
  delete: (id: number) => apiClient.delete(`/support/${id}`),
};

// ===========================
// Debug API
// ===========================

export const debugApi = {
  /**
   * Get all debug API keys (admin only)
   */
  getKeys: () => apiClient.get('/debug/api-keys'),

  /**
   * Create a new debug API key (admin only)
   */
  createKey: (data: {
    name: string;
    description?: string;
    allowed_ips?: string;
    expires_in_days?: number;
  }) => apiClient.post('/debug/api-keys', data),

  /**
   * Delete (revoke) a debug API key (admin only)
   */
  deleteKey: (keyId: number) => apiClient.delete(`/debug/api-keys/${keyId}`),

  /**
   * Get logs from the buffer (admin only)
   */
  getLogs: (params?: {
    level?: string;
    since_minutes?: number;
    search?: string;
    source?: string;
    limit?: number;
  }) => apiClient.get('/debug/logs', { params }),

  /**
   * Get Celery worker logs (admin only)
   */
  getCeleryLogs: (params?: { lines?: number; search?: string }) =>
    apiClient.get('/debug/logs/celery', { params }),
};

/**
 * Teaser-Analyse. Admins übergeben tenant_id, für alle anderen ignoriert das Backend ihn.
 */
export const teaserApi = {
  getDefaultPrompts: (tenantId?: number) =>
    apiClient.get<TeaserDefaultPrompt[]>('/teaser/default-prompts', {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    }),

  saveDefaultPrompts: (prompts: { id?: number; content: string }[], tenantId?: number) =>
    apiClient.put<TeaserDefaultPrompt[]>(
      '/teaser/default-prompts',
      { prompts },
      { params: tenantId ? { tenant_id: tenantId } : undefined }
    ),

  getModels: (tenantId?: number) =>
    apiClient.get<TeaserModelOption[]>('/teaser/models', {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    }),

  getAnalyses: (params: { limit?: number; offset?: number; tenant_id?: number }) =>
    apiClient.get<TeaserAnalysisList>('/teaser/analyses', { params }),

  createAnalysis: (data: TeaserAnalysisCreate, tenantId?: number) =>
    apiClient.post<TeaserAnalysis>('/teaser/analyses', data, {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    }),

  exportCSV: (id: number, tenantId?: number) =>
    apiClient.get(`/teaser/analyses/${id}/csv`, {
      params: tenantId ? { tenant_id: tenantId } : undefined,
      responseType: 'blob',
    }),
};

export default apiClient;
